import React, { useState, useEffect } from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  CalendarMonth,
  AccessTime,
  Business,
  Person,
  Email,
  CheckCircle,
  Cancel,
  Warning,
  Send,
} from "@mui/icons-material";
import { FiChevronDown, FiChevronUp, FiSend, FiUser } from "react-icons/fi";
import {
  getDocData,
  getDocDataSnap,
  setDocData,
  updateDoc,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import HistoryModule from "../../../components/HistoryModule";
import CommentsSection from "../../../components/CommentsSection";
import { useSelector } from "react-redux";
import { dualNotification } from "../../../utils/EleghoUtils";
import { adminEmailAddress } from "../../../utils/constants";

export default function BookingRequestDetails() {
  const currentUser = useSelector((state) => state.userObject);
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);

  useEffect(() => {
    const fetchBooking = async () => {
      await getDocDataSnap("bookings", id, (booking) => {
        setBooking(booking);
        setComments(booking.comments || []);
      });
    };
    fetchBooking();
  }, [id]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    try {
      // Check if timestamp is a Firestore timestamp with seconds property
      if (timestamp.seconds) {
        return format(new Date(timestamp.seconds * 1000), "MMMM d, yyyy");
      }

      // Check if timestamp is a Date object
      if (timestamp instanceof Date) {
        return format(timestamp, "MMMM d, yyyy");
      }

      // Check if timestamp is a string that can be parsed as a date
      const parsedDate = new Date(timestamp);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, "MMMM d, yyyy");
      }

      // If none of the above, return the original value
      return timestamp;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const formatTime = (time) => {
    try {
      // Handle 24-hour format (HH:mm)
      if (time.includes(":")) {
        const [hours, minutes] = time.split(":");
        const date = new Date();
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        return format(date, "h:mm a");
      }
      // Handle 12-hour format (HH:mm AM/PM)
      return format(new Date(`2000-01-01T${time}`), "h:mm a");
    } catch (error) {
      console.error("Error formatting time:", error);
      return time; // Return original time if formatting fails
    }
  };

  const handleCancelRequest = async () => {
    try {
      setIsProcessing(true);
      await setDocData("bookings", id, {
        status: "Cancelled",
        cancelledAt: new Date(),
      });
      toast.success("Booking request cancelled successfully");
      navigate("/facility-usage");
    } catch (error) {
      toast.error("Failed to cancel booking request");
      console.error("Error cancelling booking:", error);
    } finally {
      setIsProcessing(false);
      setShowCancelModal(false);
    }
  };
  const handleNewComment = async (commentText) => {
    if (!commentText.trim()) return;

    setIsSubmittingComment(true);

    const newCommentObj = {
      createdBy: currentUser.displayName,
      email: currentUser.email,
      createdAt: new Date().toLocaleString(),
      message: commentText,
    };

    const updatedComments = [...comments, newCommentObj];

    try {
      // Create history entry for comment
      const historyEntry = {
        action: "Comment added",
        at: new Date().toLocaleString(),
        by: booking.createdName,
        to: commentText,
      };

      // Update both comments and history in Firestore
      await setDocData("bookings", id, {
        comments: updatedComments,
        history: [...(booking.history || []), historyEntry],
      });

      setComments(updatedComments);

      const notificationObject = {
        recipient: adminEmailAddress,
        recipientName: "Admin",
        sender: currentUser.email,
        senderName: currentUser.displayName,
        subject: "New Comment on Booking Request",
        message: `New comment on booking request from ${currentUser.displayName} for booking id ${id}`,
        type: "booking comment",
        id: id,
        url: `/facilities?facility=${booking.bookingType}&filter=All`,
        read: false,
      };

      await dualNotification(notificationObject);

      toast.success("Comment added successfully");
    } catch (error) {
      toast.error("Failed to add comment");
      console.error("Error adding comment:", error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleShowMore = () => {
    setDisplayCount((prev) => prev + 5);
  };

  const handleShowLess = () => {
    setDisplayCount(5);
  };

  if (!booking) {
    return (
      <UserScreenWindow label="My Booking Request">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-eleghoBlue"></div>
        </div>
      </UserScreenWindow>
    );
  }

  // Get last 8 characters of booking ID for reference
  const lastEighthChars = id.slice(-8);

  const displayedComments = comments.slice(0, displayCount);
  const hasMore = displayCount < comments.length;

  const handleDeleteComment = async (index) => {
    if (window.confirm("Are you sure you want to delete this comment?")) {
      const updatedComments = [...comments];
      updatedComments.splice(index, 1);
      await setDocData("bookings", id, {
        comments: updatedComments,
      });
      setComments(updatedComments);
      toast.success("Comment deleted successfully");
    }
  };

  return (
    <UserScreenWindow label="My Booking Request">
      <div className="text-xs text-gray-600">
        <div className="space-y-4 text-sm text-gray-600">
          {/* Status Timeline */}
          <div className="section-box">
            <div className="section-header">Booking Progress</div>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

              {/* Timeline items */}
              <div className="space-y-6">
                {/* Booking Submitted */}
                <div className="relative flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      booking.status
                        ? "bg-eleghoGreen text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium">Booking Submitted</div>
                    <div className="text-xs text-gray-500">
                      {formatDate(booking.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Reviewed */}
                <div className="relative flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      booking.status &&
                      booking.status !== "pending" &&
                      booking.status !== "rejected"
                        ? "bg-eleghoGreen text-white"
                        : booking.status === "rejected"
                        ? "bg-red-500 text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {booking.status === "rejected" ? (
                      <Cancel className="w-5 h-5" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium">Review</div>
                    <div className="text-xs text-gray-500">
                      {booking.reviewedAt
                        ? formatDate(booking.reviewedAt)
                        : "Pending"}
                    </div>
                  </div>
                </div>

                {/* Approved */}
                <div className="relative flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      booking.status === "approved"
                        ? "bg-eleghoGreen text-white"
                        : booking.status === "pending payment"
                        ? "bg-yellow-500 text-white"
                        : booking.status === "completed" ||
                          booking.status === "deposit refunded" ||
                          booking.status === "no refund necessary"
                        ? "bg-eleghoGreen text-white"
                        : booking.status === "rejected"
                        ? "bg-red-500 text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {booking.status === "pending payment" ? (
                      <AccessTime className="w-5 h-5" />
                    ) : booking.status === "rejected" ? (
                      <Cancel className="w-5 h-5" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium">Approval</div>
                    <div className="text-xs text-gray-500">
                      {booking.status === "approved" ||
                      booking.status === "pending payment" ||
                      booking.status === "completed" ||
                      booking.status === "deposit refunded" ||
                      booking.status === "no refund necessary"
                        ? formatDate(booking.approvedAt)
                        : booking.status === "rejected"
                        ? "Rejected"
                        : "Pending"}
                    </div>
                  </div>
                </div>

                {/* Awaiting Payment - Only show if not rejected */}
                {booking.status !== "rejected" && (
                  <div className="relative flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        booking.status === "pending payment"
                          ? "bg-yellow-500 text-white"
                          : booking.paymentStatus === "paid"
                          ? "bg-eleghoGreen text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {booking.status === "pending payment" ? (
                        <AccessTime className="w-5 h-5" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium">
                        Payment of Fees and Deposits
                      </div>
                      <div className="text-xs text-gray-500">
                        {booking.paymentStatus === "paid"
                          ? formatDate(booking.paidAt)
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Paid - Only show if not rejected */}
                {booking.status !== "rejected" && (
                  <div className="relative flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        booking.paymentStatus === "paid"
                          ? "bg-eleghoGreen text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium">Paid</div>
                      <div className="text-xs text-gray-500">
                        {booking.paymentStatus === "paid"
                          ? formatDate(booking.paidAt)
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Completed - Only show if not rejected */}
                {booking.status !== "rejected" && (
                  <div className="relative flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        booking.status === "completed" ||
                        booking.status === "deposit refunded" ||
                        booking.status === "no refund necessary"
                          ? "bg-eleghoGreen text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium">Completed</div>
                      <div className="text-xs text-gray-500">
                        {booking.status === "completed"
                          ? formatDate(booking.completedAt)
                          : booking.status === "deposit refunded" ||
                            booking.status === "no refund necessary"
                          ? formatDate(booking.completedAt)
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Deposit Refunded - Only show if not rejected */}
                {booking.status !== "rejected" && (
                  <div className="relative flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        booking.status === "deposit refunded" ||
                        booking.status === "no refund necessary"
                          ? "bg-eleghoGreen text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium">
                        Refund of Deposit (if applicable)
                      </div>
                      <div className="text-xs text-gray-500">
                        {booking.status === "deposit refunded" ||
                        booking.status === "no refund necessary"
                          ? booking.status === "deposit refunded"
                            ? formatDate(booking.refundedAt)
                            : booking.status
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="section-box">
            <div className="section-header">Details of Booking</div>
            <div className="flex items-center justify-between">
              <div>Reference Number</div>
              <div>{lastEighthChars}</div>
            </div>
            <div className="flex items-center justify-between gap-8">
              <div>Facility Type</div>
              <div>{booking.bookingType}</div>
            </div>
            <div className="flex items-center justify-between gap-8">
              <div>Date</div>
              <div>{formatDate(booking.date)}</div>
            </div>
            <div className="flex items-center justify-between gap-8">
              <div>Time</div>
              <div>
                {formatTime(booking.startTime)} - {formatTime(booking.endTime)}
              </div>
            </div>
            <div className="flex items-center justify-between gap-8">
              <div>Requested On</div>
              <div>{formatDate(booking.createdAt)}</div>
            </div>
          </div>

          {/* User Information */}
          <div className="section-box">
            <div className="section-header">My Information</div>
            <div className="flex items-center justify-between gap-8">
              <div>Name</div>
              <div>{booking.createdName}</div>
            </div>
            <div className="flex items-center justify-between gap-8">
              <div>Email</div>
              <div>{booking.createdEmail}</div>
            </div>
          </div>

          {/* Assigned To Section */}
          <div className="section-box">
            <div className="section-header">Assigned To</div>
            <div className="flex items-center justify-between gap-8">
              <div>Staff</div>
              <div>
                {booking.assignedTo
                  ? booking.assignedTo.map((staff, index) => (
                      <div key={index}>{staff.label}</div>
                    ))
                  : "Not Assigned"}
              </div>
            </div>
          </div>

          {/* History Timeline using HistoryModule */}
          <HistoryModule data={booking.history || []} />

          {/* Comments Section */}
          {booking.status !== "Approved" && (
            <CommentsSection
              comments={displayedComments}
              onCommentSubmit={handleNewComment}
              isSubmitting={isSubmittingComment}
              onCommentDelete={handleDeleteComment}
              currentUser={currentUser}
              withoutDelete={true}
            />
          )}

          {/* Action Buttons */}
          {booking.status === "Pending payment" && (
            <div className="section-box">
              <div className="section-header">Actions</div>
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-1.5 text-sm border border-red-200"
                >
                  <Cancel className="w-4 h-4" />
                  Cancel Request
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-4">
              <Warning className="w-5 h-5 text-red-600" />
              <h3 className="text-sm font-medium text-gray-900">
                Confirm Cancellation
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this booking? This action cannot
              be undone.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                No, Keep Booking
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={isProcessing}
                className="flex-1 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                <Cancel className="w-4 h-4" />
                {isProcessing ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </UserScreenWindow>
  );
}
